/**
 * Announcements Mutation Hooks
 */

import { useMutation } from '@tanstack/react-query';
import { laterCursor } from '@/shared/lib/announcements/announcement-rules';
import { useAuthStore } from '@/shared/stores/auth-store';
import { announcementsClient } from '../client/announcements.client';

/**
 * Advance the What's New read cursor to `lastShownAt` (newest item the user
 * saw; omitted → now). `/me` is not TanStack-backed, so the cursor lives on
 * the auth-store user: optimistic on mutate (hides the popup/dots at once, never
 * moving backwards — same as the BE clamp), server value on success, rollback
 * on error. The
 * announcement caches stay untouched — unread is derived from the cursor, and
 * the /whats-new page still needs the read items.
 */
export function useMarkAnnouncementsRead() {
  return useMutation({
    mutationKey: ['announcements', 'mark-read'],
    mutationFn: (lastShownAt?: string) => announcementsClient.markRead(lastShownAt),
    onMutate: (lastShownAt?: string) => {
      const prev = useAuthStore.getState().user?.last_announcements_read_at ?? null;
      const next = laterCursor(prev, lastShownAt ?? new Date().toISOString());
      useAuthStore.getState().updateUser({ last_announcements_read_at: next });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) useAuthStore.getState().updateUser({ last_announcements_read_at: ctx.prev });
    },
    onSuccess: (serverReadAt) => {
      if (serverReadAt)
        useAuthStore.getState().updateUser({ last_announcements_read_at: serverReadAt });
    },
  });
}
